import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Select, Button, DatePicker, Spin, message, Input, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import ResizableTable from '../components/ResizableTable';
import CustomerSelector from '../components/CustomerSelector';
import { fetchStarLevels, getClassHourPrice } from '../utils/enumUtils';

const { Option } = Select;
const { TabPane } = Tabs;

const Academic = ({ user }) => {
  const { apiClient } = useAuth();

  // 设置 dayjs 为中文
  dayjs.locale('zh-cn');

  const [form] = Form.useForm();

  const [customerDisabled, setCustomerDisabled] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchParams, setSearchParams] = useState({});

  const [summary, setSummary] = useState({
    createUserStats: {},
    babyNameStats: {}
  });

  // 教练相关状态
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(undefined);
  
  // 星级配置相关状态
  const [starLevels, setStarLevels] = useState([]);



  // Role-based campus setting
  useEffect(() => {
    try {
      const localUserRaw = localStorage.getItem('user');
      const localUser = localUserRaw ? JSON.parse(localUserRaw) : null;
      const effectiveUser = user || localUser;

      if (effectiveUser) {
        if (effectiveUser.role && effectiveUser.role !== 'R00001') {
          setCustomerDisabled(true);
          setSelectedCustomerId(effectiveUser.customerId);
          form.setFieldsValue({ customerId: effectiveUser.customerId });
        } else if (effectiveUser.customerId) {
          // super admin or others, can change but default to their campus if present
          setSelectedCustomerId(effectiveUser.customerId);
          form.setFieldsValue({ customerId: effectiveUser.customerId });
        }
      }
    } catch (err) {
      console.error('读取用户信息失败:', err);
    }
  }, [user, form]);

  // 当API客户端可用时加载星级配置数据
  useEffect(() => {
    if (apiClient) {
      fetchStarLevels(apiClient).then(levels => {
        setStarLevels(levels);
      }).catch(err => {
        console.error('加载星级配置失败:', err);
        setStarLevels([]);
      });
    }
  }, [apiClient]);

  // Fetch first page when campus or basic filters ready
  useEffect(() => {
    if (selectedCustomerId) {
      // 默认按当前月份查询
      const defaultQueryMonth = dayjs().startOf('month');
      form.setFieldsValue({ queryMonth: defaultQueryMonth });
      const filters = {
        customerId: selectedCustomerId,
        queryMonth: defaultQueryMonth.format('YYYY-MM-DD'),
      };
      setSearchParams(filters);
      // 触发搜索，与校区切换保持一致
      handleSearch({
        customerId: selectedCustomerId,
        queryMonth: defaultQueryMonth,
        coach: undefined
      });

      // 获取教练列表
      fetchTeachers(selectedCustomerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId]);



  // 获取教练列表
  const fetchTeachers = async (customerId) => {
    if (!customerId) return;
    
    // 先尝试从localStorage获取
    const storageKey = `teacher_${customerId}`;
    const cachedData = localStorage.getItem(storageKey);
    
    if (cachedData) {
      try {
        const parsedData = JSON.parse(cachedData);
        // 检查数据是否过期（可选）
        setTeachers(parsedData.teacherList || []);
        return;
      } catch (e) {
        console.warn('解析缓存教练数据失败:', e);
      }
    }
    
    // 如果localStorage中没有或者解析失败，则调用接口获取
    try {
      const response = await apiClient.get('/sports-base-server-prod/main/getTeacherList', {
        params: {
          customerId: customerId,
          pageNum: 1,
          pageSize: 999
        }
      });
      
      if (response.data.result === '1') {
        const teacherList = response.data.teacherList || [];
        setTeachers(teacherList);
        
        // 保存到localStorage
        const cacheData = {
          teacherList: teacherList,
          timestamp: Date.now()
        };
        localStorage.setItem(storageKey, JSON.stringify(cacheData));
      } else {
        message.error('获取教练列表失败');
      }
    } catch (err) {
      console.error('获取教练列表错误:', err);
      message.error('获取教练列表失败');
    }
  };


  // 判断当前用户是否有管理员权限
  const isAdminUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      
      const user = JSON.parse(userStr);
      return user.role === 'R00001';
    } catch (err) {
      console.error('解析用户信息失败:', err);
      return false;
    }
  };

  const computeSummary = (list) => {
    if (!Array.isArray(list)) {
      setSummary({
        createUserStats: {},
        babyNameStats: {}
      });
      return;
    }

    const createUserStats = {};
    const babyNameStats = {};

    list.forEach((item) => {
      const createUser = item.createUser || '未知';
      if (!createUserStats[createUser]) {
        createUserStats[createUser] = {
          count: 0,
          completed: 0,
          notCompleted: 0,
          teachingModeStats: {},
          totalConsumeClass: 0
        };
      }
      createUserStats[createUser].count += 1;
      const consume = Number(item.consumeClass) || 0;
      createUserStats[createUser].totalConsumeClass += consume;
      if (Number(item.completeClass) === 1) {
        createUserStats[createUser].completed += 1;
      } else {
        createUserStats[createUser].notCompleted += 1;
      }
      // 授课模式统计只包含已扣课时的部分
      if (Number(item.completeClass) === 1) {
        const mode = item.teachingMode || '未设定';
        if (!createUserStats[createUser].teachingModeStats[mode]) {
          createUserStats[createUser].teachingModeStats[mode] = 0;
        }
        createUserStats[createUser].teachingModeStats[mode] += 1;
      }

      // 处理babyNameStats
      const babyName = item.babyName || '未知会员';
      if (!babyNameStats[babyName]) {
        babyNameStats[babyName] = {
          count: 0,
          completed: 0,
          notCompleted: 0,
          totalConsumeClass: 0
        };
      }
      babyNameStats[babyName].count += 1;
      babyNameStats[babyName].totalConsumeClass += consume;
      if (Number(item.completeClass) === 1) {
        babyNameStats[babyName].completed += 1;
      } else {
        babyNameStats[babyName].notCompleted += 1;
      }
    });

    setSummary({
      createUserStats,
      babyNameStats
    });
  };

  const fetchReportList = async ({ pageNo, pageSize, ...filters }) => {
    if (!apiClient) {
      message.error('系统初始化中，请稍后重试');
      return;
    }

    const customerId = filters.customerId || selectedCustomerId || (() => {
      try {
        const localUserRaw = localStorage.getItem('user');
        const localUser = localUserRaw ? JSON.parse(localUserRaw) : null;
        return localUser?.customerId;
      } catch {
        return undefined;
      }
    })();

    if (!customerId) {
      message.warning('请选择校区');
      return;
    }

    setLoading(true);
    setError(null);

    const params = {
      customerId,
      pageNo: 1,
      pageSize: 99999,
    };

    if (filters.queryMonth) {
      params.queryMonth = filters.queryMonth;
    }
    if (filters.coach) {
      params.userName = filters.coach.trim();
    }

    try {
      const response = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/reportList', 
        params
      );

      if (response.data && response.data.code === 200) {
        const data = response.data.data || {};
        const list = data.list || [];
        const total = data.totalRecord || 0;

        setDataSource(list);
        setPagination((prev) => ({
          ...prev,
          current: params.pageNo,
          pageSize: params.pageSize,
          total,
        }));
        computeSummary(list);
      } else {
        const msg = response.data && response.data.message ? response.data.message : '获取教务数据失败';
        message.error(msg);
        setError(msg);
        setDataSource([]);
        computeSummary([]);
      }
    } catch (err) {
      console.error('获取教务数据错误:', err);
      const msg = '获取教务数据失败，请稍后重试';
      message.error(msg);
      setError(msg);
      setDataSource([]);
      computeSummary([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (value) => {
    setSelectedCustomerId(value || '');
    // 当校区改变时，清空教练选择
    setSelectedTeacher(undefined);
    form.setFieldsValue({ coach: undefined });
    
    // 获取新校区的教练列表
    if (value) {
      fetchTeachers(value);
      // 校区切换时触发搜索
      handleSearch({
        customerId: value,
        queryMonth: dayjs().startOf('month'),
        coach: undefined
      });
    } else {
      setTeachers([]);
    }
  };

  const handleTeacherChange = (value) => {
    setSelectedTeacher(value);
  };

  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = (values) => {
    // 防止重复查询
    if (isSearching) return;
    setIsSearching(true);
    
    // 每次查询前清除列表数据
    setDataSource([]);
    setSummary({
      createUserStats: {},
      babyNameStats: {}
    });
    
    // 检查查询月份是否填写
    if (!values.queryMonth) {
      message.warning('查询月份为必填项');
      return;
    }
    
    const customerId = values.customerId || selectedCustomerId;
    const queryMonth = values.queryMonth ? values.queryMonth.format('YYYY-MM-DD') : undefined;
    const coach = values.coach ? values.coach.trim() : undefined;

    // 更新选中的教练
    setSelectedTeacher(values.coach);

    const filters = {
      customerId,
      ...(queryMonth ? { queryMonth } : {}),
      ...(coach ? { coach } : {}),
    };

    setSearchParams(filters);
    const pageSize = pagination.pageSize;
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchReportList({ pageNo: 1, pageSize, ...filters }).finally(() => {
      setIsSearching(false);
    });
  };

  // 添加列状态管理
  const [columns, setColumns] = useState([
    {
      title: 'No.',
      key: 'index',
      width: 80,
      fixed: 'left',
      render: (text, record, index) => index + 1,
    },
    {
      title: '会员',
      dataIndex: 'babyName',
      key: 'babyName',
      width: 150,
      sorter: (a, b) => {
        const nameA = a.babyName || '';
        const nameB = b.babyName || '';
        return nameA.localeCompare(nameB);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索会员"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            搜索
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            重置
          </Button>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => {
        const name = record.babyName || '';
        return name.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: '上课日期',
      dataIndex: 'trainDate',
      key: 'trainDate',
      width: 200,
      sorter: (a, b) => {
        const dateA = new Date(a.trainDate || '');
        const dateB = new Date(b.trainDate || '');
        return dateA - dateB;
      },
      render: (text) => {
        if (!text) return '-';
        const d = dayjs(text);
        return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : text;
      },
    },
    {
      title: '训练课时',
      dataIndex: 'trainTimes',
      key: 'trainTimes',
      width: 120,
      sorter: (a, b) => {
        const timesA = parseFloat(a.trainTimes) || 0;
        const timesB = parseFloat(b.trainTimes) || 0;
        return timesA - timesB;
      },
    },
    {
      title: '消耗课时',
      dataIndex: 'consumeClass',
      key: 'consumeClass',
      width: 120,
      sorter: (a, b) => {
        const consumeA = parseFloat(a.consumeClass) || 0;
        const consumeB = parseFloat(b.consumeClass) || 0;
        return consumeA - consumeB;
      },
    },
    {
      title: '是否结课',
      dataIndex: 'completeClass',
      key: 'completeClass',
      width: 120,
      sorter: (a, b) => {
        const completeA = parseInt(a.completeClass) || 0;
        const completeB = parseInt(b.completeClass) || 0;
        return completeA - completeB;
      },
      render: (value) => {
        const num = Number(value);
        if (num === 1) return '已结课';
        if (num === 0) return '未结课';
        return '-';
      },
    },
    {
      title: '授课教练',
      dataIndex: 'createUser',
      key: 'createUser',
      width: 150,
      sorter: (a, b) => {
        const userA = a.createUser || '';
        const userB = b.createUser || '';
        return userA.localeCompare(userB);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索教练"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            搜索
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            重置
          </Button>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => {
        const user = record.createUser || '';
        return user.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: '授课模式',
      dataIndex: 'teachingMode',
      key: 'teachingMode',
      width: 150,
      sorter: (a, b) => {
        const modeA = a.teachingMode || '';
        const modeB = b.teachingMode || '';
        return modeA.localeCompare(modeB);
      },
      render: (text) => text || '-',
    },
    {
      title: '有效课时',
      key: 'effectiveClassHours',
      width: 120,
      sorter: (a, b) => {
        // 计算换算有效课时 - 直接根据标准模式计算
        const calculateEffectiveHours = (record) => {
          const mode = record.teachingMode || '未指定';
          const consumeClass = parseFloat(record.consumeClass) || 0;
          
          if (mode === 'SVIP专属' || mode === 'VIP私教') {
            return consumeClass * 1; // 按1:1换算
          } else if (mode === '私教小团' || mode === '未指定') {
            return consumeClass * 0.7; // 按1:0.7换算
          }
          return consumeClass * 0.7; // 默认按0.7换算
        };
        
        const hoursA = calculateEffectiveHours(a);
        const hoursB = calculateEffectiveHours(b);
        return hoursA - hoursB;
      },
      render: (text, record) => {
        // 计算换算有效课时 - 直接根据标准模式计算
        const mode = record.teachingMode || '未指定';
        const consumeClass = parseFloat(record.consumeClass) || 0;
        
        let effectiveHours = 0;
        if (mode === 'SVIP专属' || mode === 'VIP私教') {
          effectiveHours = consumeClass * 1; // 按1:1换算
        } else if (mode === '私教小团' || mode === '未指定') {
          effectiveHours = consumeClass * 0.7; // 按1:0.7换算
        } else {
          effectiveHours = consumeClass * 0.7; // 默认按0.7换算
        }
        
        return effectiveHours.toFixed(1);
      },
    },
  ]);

  const createUserEntries = Object.entries(summary.createUserStats || {});
  const babyNameEntries = Object.entries(summary.babyNameStats || {}).sort((a, b) => b[1].count - a[1].count);

  return (
    <div className="menu-item-content">
      <Card
        title="教务数据列表"
        style={{ margin: '16px' }}
      >
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item label="校区" name="customerId">
            <CustomerSelector
              style={{ width: 350 }}
              placeholder="请选择校区"
              onChange={handleCustomerChange}
              disabled={customerDisabled}
              allowClear={!customerDisabled}
              showTeamSelector={false}
            />
          </Form.Item>
          <Form.Item 
            label="查询月份" 
            name="queryMonth"
            rules={[{ required: true, message: '请选择查询月份' }]}
          >
            <DatePicker
              picker="month"
              allowClear={false}
              style={{ width: 180 }}
            />
          </Form.Item>
          <Form.Item label="教练" name="coach">
            <Select
              style={{ width: 180 }}
              placeholder="请选择教练"
              allowClear
              showSearch
              optionFilterProp="children"
              onChange={handleTeacherChange}
              value={selectedTeacher}
            >
              {teachers.map((teacher) => (
                <Option key={teacher.userId} value={teacher.userName}>
                  {teacher.userName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              搜索
            </Button>
          </Form.Item>
        </Form>

        {error && (
          <div style={{ color: 'red', marginBottom: 16 }}>{error}</div>
        )}

        {(createUserEntries.length > 0 || babyNameEntries.length > 0) && (
          <Tabs defaultActiveKey="coach" style={{ marginBottom: 16 }}>
            <TabPane tab="教练统计" key="coach">
              {createUserEntries.length > 0 ? (
                <Card type="inner">
                  <Table
                    dataSource={createUserEntries}
                    pagination={false}
                    rowKey={[0]}
                    locale={{ emptyText: '暂无数据' }}
                    columns={[
                      {
                        title: (
                            <>
                              教练<br />
                              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>&nbsp;</span>
                            </>
                        ),
                        dataIndex: '0',
                        key: 'coach',
                        render: (text) => text || '-',
                      },
                      {
                        title: (
                            <>
                              总排课数<br />
                              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>&nbsp;</span>
                            </>
                        ),
                        key: 'totalCount',
                        sorter: (a, b) => a[1].count - b[1].count,
                        render: (text, record) => record[1].count,
                      },
                      {
                        title: (
                            <>
                              已扣课时<br />
                              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>&nbsp;</span>
                            </>
                        ),
                        key: 'completed',
                        sorter: (a, b) => a[1].completed - b[1].completed,
                        render: (text, record) => record[1].completed,
                      },
                      {
                        title: (
                            <>
                              未扣课时<br />
                              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>需要确认</span>
                            </>
                        ),
                        key: 'notCompleted',
                        sorter: (a, b) => a[1].notCompleted - b[1].notCompleted,
                        render: (text, record) => record[1].notCompleted,
                      },
                      {
                        title: (
                            <>
                              总消耗课时<br />
                              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>含未扣课时</span>
                            </>
                        ),
                        key: 'totalConsumeClass',
                        sorter: (a, b) => a[1].totalConsumeClass - b[1].totalConsumeClass,
                        render: (text, record) => record[1].totalConsumeClass,
                      },
                      {
                        title: (
                          <>
                            授课模式<br />
                            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>只统计已扣课时</span>
                          </>
                        ),
                        key: 'teachingMode',
                        render: (text, record) => Object.entries(record[1].teachingModeStats || {}).map(([mode, cnt]) => `${mode}: ${cnt}`).join(', '),
                      },
                      {
                        title: (
                            <>
                              有效课时<br />
                              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>&nbsp;</span>
                            </>
                        ),
                        key: 'effectiveClassHours',
                        sorter: (a, b) => {
                          // 计算换算有效课时 - 直接根据标准模式计算
                          const calculateEffectiveHours = (stats) => {
                            let total = 0;
                            Object.entries(stats || {}).forEach(([mode, count]) => {
                              if (mode === 'SVIP专属' || mode === 'VIP私教') {
                                total += count * 1; // 按1:1换算
                              } else if (mode === '私教小团' || mode === '未指定') {
                                total += count * 0.7; // 按1:0.7换算
                              }
                            });
                            return total;
                          };
                          
                          const hoursA = calculateEffectiveHours(a[1].teachingModeStats);
                          const hoursB = calculateEffectiveHours(b[1].teachingModeStats);
                          return hoursA - hoursB;
                        },
                        render: (text, record) => {
                          // 计算换算有效课时 - 直接根据标准模式计算
                          let total = 0;
                          Object.entries(record[1].teachingModeStats || {}).forEach(([mode, count]) => {
                            if (mode === 'SVIP专属' || mode === 'VIP私教') {
                              total += count * 1; // 按1:1换算
                            } else if (mode === '私教小团' || mode === '未指定') {
                              total += count * 0.7; // 按1:0.7换算
                            }
                          });
                          return total.toFixed(1);
                        },
                      },
                      ...(isAdminUser() ? [{
                        title: (
                            <>
                              星级(课时费)<br />
                              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>&nbsp;</span>
                            </>
                        ),
                        key: 'starLevel',
                        width: 150,
                        render: (text, record) => {
                          // 直接使用第一列的教练姓名
                          const coachName = record[0];
                          const coach = teachers.find(t => t.userName === coachName);
                          
                          if (!coach || !coach.starLevel) {
                            return '未设置';
                          }
                          
                          // 从缓存获取星级配置数据
                          let starLevelsFromCache = null;
                          try {
                            const cachedStarLevels = localStorage.getItem('STAR_LEVEL');
                            if (cachedStarLevels) {
                              starLevelsFromCache = JSON.parse(cachedStarLevels);
                            }
                          } catch (e) {
                            console.error('解析STAR_LEVEL缓存失败:', e);
                          }
                          
                          // 如果缓存存在且有效，使用缓存数据；否则使用默认映射
                          const useStarLevels = starLevelsFromCache && Array.isArray(starLevelsFromCache) && starLevelsFromCache.length > 0 ? starLevelsFromCache : [];
                          
                          // 获取课时费单价
                          const pricePerHour = getClassHourPrice(coach.starLevel, useStarLevels);
                          if (pricePerHour === null) {
                            return `${coach.starLevel} * ?元/课时`;
                          }
                          
                          return `${coach.starLevel} (${pricePerHour}元/课时)`;
                        },
                      }, {
                        title: (
                            <>
                              课时费<br />
                              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>&nbsp;</span>
                            </>
                        ),
                        key: 'classHourFee',
                        width: 120,
                        sorter: (a, b) => {
                          // 计算课时费
                          const calculateClassHourFee = (record) => {
                            // 直接使用第一列的教练姓名
                            const coachName = record[0];
                            const coach = teachers.find(t => t.userName === coachName);
                            
                            if (!coach || !coach.starLevel) {
                              return 0;
                            }
                            
                            // 从缓存获取星级配置数据
                            let starLevelsFromCache = null;
                            try {
                              const cachedStarLevels = localStorage.getItem('STAR_LEVEL');
                              if (cachedStarLevels) {
                                starLevelsFromCache = JSON.parse(cachedStarLevels);
                              }
                            } catch (e) {
                              console.error('解析STAR_LEVEL缓存失败:', e);
                            }
                            
                            // 如果缓存存在且有效，使用缓存数据；否则使用默认映射
                            const useStarLevels = starLevelsFromCache && Array.isArray(starLevelsFromCache) && starLevelsFromCache.length > 0 ? starLevelsFromCache : [];
                            
                            // 获取课时费单价
                            const pricePerHour = getClassHourPrice(coach.starLevel, useStarLevels);
                            if (pricePerHour === null) {
                              return 0;
                            }
                            
                            // 计算换算有效课时
                            let effectiveHours = 0;
                            Object.entries(record[1].teachingModeStats || {}).forEach(([mode, count]) => {
                              if (mode === 'SVIP专属' || mode === 'VIP私教') {
                                effectiveHours += count * 1;
                              } else if (mode === '私教小团' || mode === '未指定') {
                                effectiveHours += count * 0.7;
                              }
                            });
                            
                            return effectiveHours * pricePerHour;
                          };
                          
                          const feeA = calculateClassHourFee(a);
                          const feeB = calculateClassHourFee(b);
                          return feeA - feeB;
                        },
                        render: (text, record) => {
                          // 直接使用第一列的教练姓名
                          const coachName = record[0];
                          
                          // 从缓存的教练列表中查找该教练的星级信息
                          const coach = teachers.find(t => t.userName === coachName);
                          
                          // 如果找不到教练信息或没有星级，则显示提示
                          if (!coach || !coach.starLevel) {
                            return '该教练没有设置星级，无法计算课时';
                          }
                          
                          // 从缓存获取星级配置数据
                          let starLevelsFromCache = null;
                          try {
                            const cachedStarLevels = localStorage.getItem('STAR_LEVEL');
                            if (cachedStarLevels) {
                              starLevelsFromCache = JSON.parse(cachedStarLevels);
                            }
                          } catch (e) {
                            console.error('解析STAR_LEVEL缓存失败:', e);
                          }
                          
                          // 如果缓存存在且有效，使用缓存数据；否则使用默认映射
                          const useStarLevels = starLevelsFromCache && Array.isArray(starLevelsFromCache) && starLevelsFromCache.length > 0 ? starLevelsFromCache : [];
                          
                          // 获取课时费单价
                          const pricePerHour = getClassHourPrice(coach.starLevel, useStarLevels);
                          if (pricePerHour === null) {
                            return '该教练没有设置星级，无法计算课时';
                          }
                          
                          // 计算换算有效课时
                          let effectiveHours = 0;
                          Object.entries(record[1].teachingModeStats || {}).forEach(([mode, count]) => {
                            if (mode === 'SVIP专属' || mode === 'VIP私教') {
                              effectiveHours += count * 1;
                            } else if (mode === '私教小团' || mode === '未指定') {
                              effectiveHours += count * 0.7;
                            }
                          });
                          
                          const totalFee = effectiveHours * pricePerHour;
                          return `¥${totalFee.toFixed(2)}`;
                        },
                      }] : []),
                    ]}
                  />
                </Card>
              ) : (
                <div>暂无教练统计数据</div>
              )}
            </TabPane>
            <TabPane tab="会员统计" key="member">
              {babyNameEntries.length > 0 ? (
                <Card type="inner">
                  <Table
                    dataSource={babyNameEntries}
                    pagination={false}
                    rowKey={[0]}
                    locale={{ emptyText: '暂无数据' }}
                    columns={[
                      {
                        title: '学员',
                        dataIndex: '0',
                        key: 'babyName',
                        render: (text) => text || '-',
                      },
                      {
                        title: '总排课数',
                        key: 'totalCount',
                        sorter: (a, b) => a[1].count - b[1].count,
                        render: (text, record) => record[1].count,
                      },
                      {
                        title: '已扣课时',
                        key: 'completed',
                        sorter: (a, b) => a[1].completed - b[1].completed,
                        render: (text, record) => record[1].completed,
                      },
                      {
                        title: '未扣课时',
                        key: 'notCompleted',
                        sorter: (a, b) => a[1].notCompleted - b[1].notCompleted,
                        render: (text, record) => record[1].notCompleted,
                      },
                      {
                        title: '总消耗课时',
                        key: 'totalConsumeClass',
                        sorter: (a, b) => a[1].totalConsumeClass - b[1].totalConsumeClass,
                        render: (text, record) => record[1].totalConsumeClass,
                      }
                    ]}
                  />
                </Card>
              ) : (
                <div>暂无会员统计数据</div>
              )}
            </TabPane>
          </Tabs>
        )}

        <Spin spinning={loading}>
          <ResizableTable
            dataSource={dataSource}
            columns={columns}
            onColumnsChange={setColumns}
            rowKey={(record) => `${record.babyId || ''}_${record.trainDate || ''}`}
            scroll={{ x: 'max-content' }}
            pagination={false}
            locale={{ emptyText: '暂无教务数据' }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default Academic;

